import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import '../databases/databases.css';

const TenantMaintenance = ({ tenantId }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showNewRequestModal, setShowNewRequestModal] = useState(false);
    const [newRequest, setNewRequest] = useState({
        tenant_id: tenantId,
        property_id: '',
        unit_number: '',
        request_type: '',
        priority: 'medium',
        description: '',
        scheduled_date: '',
        assigned_to: '',
        estimated_cost: ''
    });
    const [properties, setProperties] = useState([]);

    useEffect(() => {
        fetchRequests();
        fetchProperties();
    }, [tenantId]);

    const fetchRequests = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/maintenance-requests?tenant_id=${tenantId}`);
            if (!response.ok) throw new Error('Failed to fetch maintenance requests');
            const data = await response.json();
            setRequests(data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const fetchProperties = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/properties`);
            if (!response.ok) throw new Error('Failed to fetch properties');
            const data = await response.json();
            setProperties(data);
        } catch (err) {
            console.error('Error fetching properties:', err);
        }
    };

    const handleNewRequestChange = (e) => {
        const { name, value } = e.target;
        setNewRequest(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitNewRequest = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_BASE_URL}/api/maintenance-requests`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newRequest)
            });

            if (!response.ok) throw new Error('Failed to create maintenance request');

            setShowNewRequestModal(false);
            setNewRequest({
                tenant_id: tenantId,
                property_id: '',
                unit_number: '',
                request_type: '',
                priority: 'medium',
                description: '',
                scheduled_date: '',
                assigned_to: '',
                estimated_cost: ''
            });
            fetchRequests();
        } catch (err) {
            setError(err.message);
        }
    };

    const getPriorityClass = (priority) => {
        switch (priority.toLowerCase()) {
            case 'high': return 'priority-high';
            case 'medium': return 'priority-medium';
            case 'low': return 'priority-low';
            default: return '';
        }
    };

    const getStatusClass = (status) => {
        switch (status.toLowerCase()) {
            case 'pending': return 'status-pending';
            case 'in_progress': return 'status-in-progress';
            case 'completed': return 'status-completed';
            default: return '';
        }
    };

    if (loading) return (
        <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading...</p>
        </div>
    );
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="info-section">
            <div className="section-header">
                <h2>Maintenance Requests</h2>
                <button className="add-attribute-button" onClick={() => setShowNewRequestModal(true)}>
                    New Request
                </button>
            </div>

            {requests.length === 0 ? (
                <p className="empty-state">No maintenance requests for this tenant.</p>
            ) : (
                <div className="requests-grid">
                    {requests.map(request => (
                        <div key={request.id} className="request-card">
                            <div className="request-header">
                                <h3>{request.request_type}</h3>
                                <span className={`priority-badge ${getPriorityClass(request.priority)}`}>
                                    {request.priority}
                                </span>
                            </div>
                            <p className="unit-number">Unit: {request.unit_number || 'N/A'}</p>
                            <p className="request-type">Property: {request.property_name}</p>
                            <p className="description">{request.description}</p>
                            <div className="request-footer">
                                <span>Status: <span className={`status-badge ${getStatusClass(request.status)}`}>{request.status}</span></span>
                                <span>Created: {new Date(request.created_at).toLocaleDateString()}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showNewRequestModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2 className="notion-modal-title">Add New Maintenance Request</h2>
                        <form onSubmit={handleSubmitNewRequest}>
                            <div className="notion-modal-field">
                                <label>Property *</label>
                                <select
                                    name="property_id"
                                    value={newRequest.property_id}
                                    onChange={handleNewRequestChange}
                                    className="notion-input"
                                    required
                                >
                                    <option value="">Select Property</option>
                                    {properties.map(property => (
                                        <option key={property.id} value={property.id}>
                                            {property['Property Name'] || property.property_name || 'Unnamed Property'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="notion-modal-field">
                                <label>Unit Number</label>
                                <input
                                    type="text"
                                    name="unit_number"
                                    value={newRequest.unit_number}
                                    onChange={handleNewRequestChange}
                                    className="notion-input"
                                />
                            </div>
                            <div className="notion-modal-field">
                                <label>Request Type *</label>
                                <input
                                    type="text"
                                    name="request_type"
                                    value={newRequest.request_type}
                                    onChange={handleNewRequestChange}
                                    className="notion-input"
                                    required
                                />
                            </div>
                            <div className="notion-modal-field">
                                <label>Priority *</label>
                                <select
                                    name="priority"
                                    value={newRequest.priority}
                                    onChange={handleNewRequestChange}
                                    className="notion-input"
                                    required
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </div>
                            <div className="notion-modal-field">
                                <label>Description *</label>
                                <textarea
                                    name="description"
                                    value={newRequest.description}
                                    onChange={handleNewRequestChange}
                                    className="notion-input"
                                    required
                                ></textarea>
                            </div>
                            <div className="notion-modal-field">
                                <label>Scheduled Date</label>
                                <input
                                    type="date"
                                    name="scheduled_date"
                                    value={newRequest.scheduled_date}
                                    onChange={handleNewRequestChange}
                                    className="notion-input"
                                />
                            </div>
                            <div className="notion-modal-field">
                                <label>Assigned To</label>
                                <input
                                    type="text"
                                    name="assigned_to"
                                    value={newRequest.assigned_to}
                                    onChange={handleNewRequestChange}
                                    className="notion-input"
                                />
                            </div>
                            <div className="notion-modal-field">
                                <label>Estimated Cost</label>
                                <input
                                    type="number"
                                    name="estimated_cost"
                                    value={newRequest.estimated_cost}
                                    onChange={handleNewRequestChange}
                                    className="notion-input"
                                />
                            </div>
                            <div className="notion-modal-actions">
                                <button type="button" className="cancel-button" onClick={() => setShowNewRequestModal(false)}>Cancel</button>
                                <button type="submit" className="notion-button">Add Request</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TenantMaintenance;